import {Button, Card, CardBody, CardHeader, Typography,} from "@material-tailwind/react";
import React, {useEffect, useState} from "react";
import {BackOfficePersonGrid} from "./grid";
import {BackOfficePersonForm} from "./form";
import {CardFooterPaginator} from "../../system/cardFooterPaginator";
import {getBackOfficePerson} from "../../../services/back_office/person";
import SelectInput from "../../system/select";
import {getPersonsNameAndFamily} from "../../../services/profile/profile";
import {PaginateContext} from "../../../context/paginate";
import {LoadingOverlay} from "../../system/loading_overlay";
import persianNumberFormat from "../../../helper/PersianNumberFormat";
import {BsFilter, BsPersonPlus, BsPeople} from "react-icons/bs";

export function BackOfficePersonManagement() {

  const [people, setPeople] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [gridRowCount, setGridRowCount] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridSelectedRow, setGridSelectedRow] = useState(null);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState("id")
  const [sortType, setSortType] = useState(false);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState((gridCurrentPage) * gridRowCount);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(Date.now());

  const [personId, setPersonId] = useState(null);

  useEffect(() => {
    if ((gridCurrentPage) * gridRowCount <= gridTotalRowCount) {
      setTo((gridCurrentPage) * gridRowCount)
    } else {
      setTo(gridTotalRowCount)
    }
    if (gridTotalRowCount === 0) {
      setFrom(0)
    } else {
      setFrom((gridCurrentPage - 1) * gridRowCount + 1)
    }
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(paginate) {
    setLoading(true);
    try {
      const result = await getBackOfficePerson(paginate);
      if (result.status === 200) {
        const {peronList, total_count} = result.data;
        setPeople(peronList || []);
        setGridTotalRowCount(total_count)
      }
    } finally {
      setLoading(false);
    }
  }

  const openNewPersonModal = () => {
    setGridSelectedRow(null);
    setModalOpen(true);
  };

  const clearFilters = () => {
    setPersonId(null);
    setGridCurrentPage(1);
    setFilterResetKey(Date.now());
  };

  useEffect(() => {
    // const data = filter()
    const paginateParams = new URLSearchParams({
      "sort_by": sortBy,
      "sort_type": sortType === false ? "DESC" : "ASC",
      "limit": parseInt(gridRowCount),
      "offset": (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
    })

    const filterParams = new URLSearchParams({})

    if (personId) {
      filterParams.append("person_id", personId)
    }

    for (const [key, value] of filterParams.entries()) {
      paginateParams.append(key, value);
    }

    const paginate = paginateParams.toString()

    getData(paginate)
  }, [personId, gridCurrentPage, gridRowCount, sortBy, sortType, lastDataRefresh]);

  return (
    <PaginateContext.Provider value={{
      setSortBy: setSortBy,
      sortType: sortType,
      setSortType: setSortType,
      active: gridCurrentPage,
      setActive: setGridCurrentPage,
      totalRows: gridTotalRowCount,
      rowsCount: gridRowCount,
      setRowsCount: setGridRowCount,
      from: from,
      to: to,
    }}>
      <div className="flex w-full flex-col gap-4 p-4">
        {modalOpen && <BackOfficePersonForm person={gridSelectedRow} open={modalOpen} setOpen={setModalOpen}
                                            setLastDataRefresh={setLastDataRefresh}
                                            setGridSelectedRow={setGridSelectedRow}/>}

        <Card className="w-full">
          <CardHeader floated={false} shadow={false} className="m-0 w-full rounded-none px-6 pb-0 pt-6">
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-gray-50 text-blue-gray-700">
                  <BsPeople className="h-5 w-5"/>
                </div>
                <div className="min-w-0">
                  <Typography variant="h5" color="blue-gray">
                    ایجاد و اصلاح کاربران
                  </Typography>
                  <Typography variant="small" color="gray" className="mt-1 font-normal">
                    {gridTotalRowCount > 0
                      ? `${persianNumberFormat(gridTotalRowCount, true)} کاربر`
                      : "بدون کاربر"}
                  </Typography>
                </div>
              </div>

              <Button
                onClick={openNewPersonModal}
                color="gray"
                className="flex w-full shrink-0 items-center justify-center gap-2 sm:w-auto"
              >
                <BsPersonPlus className="h-4 w-4"/>
                جدید
              </Button>
            </div>
          </CardHeader>

          <CardBody>
            <div className="rounded-lg border border-blue-gray-50 bg-blue-gray-50/30 p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <BsFilter className="h-4 w-4 text-blue-gray-500"/>
                  <Typography variant="small" color="blue-gray" className="font-bold">
                    فیلترها
                  </Typography>
                </div>

                {personId &&
                  <Button variant="text" color="gray" size="sm" onClick={clearFilters}>
                    پاک کردن
                  </Button>
                }
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                <SelectInput
                  key={filterResetKey}
                  label={"کاربر"}
                  defaultValue={personId}
                  autoLoadService={getPersonsNameAndFamily}
                  autoLoadRender={({name, family, phone}) => {
                    const fullName = `${name?.trim()} ${family?.trim()}`.trim();
                    return fullName + " - " + phone
                  }}
                  onChange={(value) => {
                    setPersonId(value);
                    setGridCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="w-full">
          <CardBody className="w-full overflow-x-auto p-0">
            {loading && <LoadingOverlay/>}

            <div className="relative">
              <BackOfficePersonGrid
                people={people}
                setModalOpen={setModalOpen}
                setSelectedRow={setGridSelectedRow}
              />
            </div>
          </CardBody>

          <CardFooterPaginator/>
        </Card>
      </div>
    </PaginateContext.Provider>
  )
    ;
}
